import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
} from '@angular/core';
import { atxIsMobile$ } from '@epicuro-next/ataraxia/breakpoint';
import {
  pulseConfigurationDefaultValues,
  PulseConfigurationKeys,
  PulseConfigurations,
  PULSE_FEATURE_NAME,
} from '@epicuro-next/constants/rule-engine/pulse-rules';
import {
  configStripConfigurationDefaultValues,
  ConfigStripConfigurationKeys,
  ConfigStripConfigurations,
  ConfigurationsService,
  CONFIG_STRIP_FEATURE,
  selectConfigurationValueFrom,
  selectProductImageMissingVisual,
} from '@epicuro-next/platform/configuration';
import {
  clearShopQuantities,
  copyQuantities,
  DeliveryItem,
  selectAdvancedMdo,
  selectCurrentlyActiveDeliveryItems,
  selectCurrentlySelectedVariantId,
  selectDataConfigStripState,
  selectDeliveryItemsMapToShops,
  selectDeliveryShops,
  selectPrepack,
  selectPrepacksForCurrentDeliveryPeriod,
  selectScaleSets,
  selectSelectedScaleOrDefaultAvailable,
  selectShopDeliveryItemTotals,
  selectUIAvailableInventoriesForCurrentDeliveryPeriod,
  selectVariant,
  setProductNote,
  setProductQuantity,
} from '@epicuro-next/state/config-strip-state';
import * as dabState from '@epicuro-next/state/dab-state';
import { getDeliveryShopId } from '@epicuro-next/utilities';
import { select, Store } from '@ngrx/store';
import { BasketAccountAddress } from 'graphql-api/baskets-api-interfaces';
import { combineLatest, Subject } from 'rxjs';
import {
  map,
  mergeMap,
  pluck,
  shareReplay,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';

const MAX_INPUT_VALUE = 9999;

@Component({
  selector: 'epc-pre-pack-configurator',
  templateUrl: './pre-pack-configurator.component.html',
  styleUrls: ['./pre-pack-configurator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PrePackConfiguratorComponent implements OnDestroy {
  private destroy$ = new Subject<void>();

  @Input() public compactMdo: boolean;

  public prepacks$ = this.store.pipe(
    select(selectPrepacksForCurrentDeliveryPeriod),
  );

  public readonly productImageMissingVisualConfig$ = this.store.select(
    selectProductImageMissingVisual,
  );

  public shopTotals$ = this.store.pipe(select(selectShopDeliveryItemTotals));

  public showAccountId$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        PulseConfigurationKeys.DeliveryShowAccountId,
        PulseConfigurations
      >(
        PULSE_FEATURE_NAME,
        PulseConfigurationKeys.DeliveryShowAccountId,
        pulseConfigurationDefaultValues[
          PulseConfigurationKeys.DeliveryShowAccountId
        ],
      ),
    ),
  );

  public shops$ = this.store.select(selectDeliveryShops).pipe(
    withLatestFrom(this.showAccountId$),
    map(([shops, showAccountId]) => {
      const idField = showAccountId ? 'externalAccountId' : 'realAddressId';
      const fallBackField = 'addressId';
      return (shops ?? []).map((deliverable) => ({
        ...deliverable,
        id: getDeliveryShopId({
          accountId: deliverable.accountId,
          addressId: deliverable.addressId,
        }),
        displayId: deliverable[idField] || deliverable[fallBackField],
      }));
    }),
  );

  public readonly isAdvancedMdo$ = combineLatest([
    this.store.pipe(select(selectAdvancedMdo)),
    atxIsMobile$,
  ]).pipe(map(([isAmdo, isMobile]) => isAmdo && !isMobile));

  public availableInventoriesForCurrentDeliveryPeriod$ = this.store.pipe(
    select(selectUIAvailableInventoriesForCurrentDeliveryPeriod(true)),
  );

  public deliveryItems$ = this.store.pipe(
    select(selectCurrentlyActiveDeliveryItems),
  );

  public allDeliveryItems$ = this.store.pipe(
    select(selectDeliveryItemsMapToShops),
  );

  public scaleSets$ = this.store.pipe(
    select(selectScaleSets),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public selectedScale$ = this.store.pipe(
    select(selectSelectedScaleOrDefaultAvailable),
  );

  public selectedVariant$ = this.store.pipe(
    select(selectCurrentlySelectedVariantId),
  );

  public dabsData$ = this.prepacks$.pipe(
    mergeMap((prepacks) => {
      const products = prepacks.map((prepack) => ({
        productId: prepack.productId,
        brandId: prepack.brandId,
      }));
      return this.store.pipe(select(dabState.selectDabs, products));
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
    takeUntil(this.destroy$),
  );

  public showAddNote$ = this.configsService.getConfigValue({
    namespace: CONFIG_STRIP_FEATURE,
    featureName: ConfigStripConfigurationKeys.ShowPrepackNote,
    defaultValue: false,
  });

  public materialTitleAttributes$ = this.configsService
    .getConfigValue({
      namespace: CONFIG_STRIP_FEATURE,
      featureName: ConfigStripConfigurationKeys.MaterialTitleAttributes,
      defaultValue:
        configStripConfigurationDefaultValues[
          ConfigStripConfigurationKeys.MaterialTitleAttributes
        ],
    })
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  public differentMaterialDimensions$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        ConfigStripConfigurationKeys.DifferentMaterialDimensions,
        ConfigStripConfigurations
      >(
        CONFIG_STRIP_FEATURE,
        ConfigStripConfigurationKeys.DifferentMaterialDimensions,
        configStripConfigurationDefaultValues[
          ConfigStripConfigurationKeys.DifferentMaterialDimensions
        ],
      ),
    ),
  );

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public getDabsByPrepack(prepackId: string) {
    return this.dabsData$.pipe(
      take(1),
      map((data) => data[prepackId]),
      takeUntil(this.destroy$),
    );
  }

  public getMaxAvailableQuantity(prepackId: string) {
    return combineLatest([
      this.availableInventoriesForCurrentDeliveryPeriod$.pipe(pluck(prepackId)),
      this.deliveryItems$.pipe(pluck('items', prepackId)),
    ]).pipe(
      map(
        ([availableInventory, deliveryItem]: [
          { totals: number },
          DeliveryItem,
        ]) => {
          const result =
            (availableInventory?.totals || 0) + (deliveryItem?.quantity || 0);
          return result === Number.POSITIVE_INFINITY ? MAX_INPUT_VALUE : result;
        },
      ),
    );
  }

  public onQuantityChange(
    productId: string,
    quantity: number,
    deliveryShop?: BasketAccountAddress,
  ) {
    this.store
      .pipe(
        select(selectDataConfigStripState),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe((data) =>
        this.store.dispatch(
          setProductQuantity({
            changes: [
              {
                productId,
                quantity,
              },
            ],
            deliveryShop,
            data,
          }),
        ),
      );
  }

  public onNoteChange(productId: string, note: string) {
    this.selectPrepack(productId);
    this.store.dispatch(
      setProductNote({
        productId,
        note,
      }),
    );
  }

  public trackByProductId(_: number, sellable: { productId: string }) {
    return sellable.productId;
  }

  public selectVariant(productId: string) {
    this.store.dispatch(selectVariant({ productId }));
  }

  public selectPrepack(productId: string) {
    this.store.dispatch(selectPrepack({ productId }));
  }

  public copyQuantities({
    from,
    to,
  }: {
    from: {
      productId: string;
      shopId: string;
    };
    to: {
      shops: string[];
      products: string[];
    };
  }) {
    this.store
      .pipe(
        select(selectDataConfigStripState),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe((data) =>
        this.store.dispatch(copyQuantities({ from, to, data })),
      );
  }

  public clearShop(shop: BasketAccountAddress) {
    const shopId = getDeliveryShopId({
      accountId: shop.accountId,
      addressId: shop.addressId,
    });
    this.store
      .pipe(
        select(selectDataConfigStripState),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe((data) =>
        this.store.dispatch(clearShopQuantities({ shopId, data })),
      );
  }

  constructor(
    private store: Store,
    private configsService: ConfigurationsService,
  ) {}
}
